import * as THREE from 'three';
import { PLUNDER } from '@somnhold/content';
import { simToWorld, WORLD_PER_HEX } from '@somnhold/render';
import { plunderCanLeave, type BattleState, type Hero, type PlunderCache } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h, viewH, viewW } from '../ui/dom.ts';

/** A chevron pointing down; turned to point at a place off screen (the crypt's arrows). */
const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l7 8 7-8"/></svg>';

const near = (h: Hero, p: { x: number; y: number }, r: number): boolean => (h.x - p.x) ** 2 + (h.y - p.y) ** 2 <= r * r;

/** What the carry button does on a plunder right now: put down what is in hand, take a cache at hand, or nothing. */
export const plunderCarryAction = (s: BattleState, hero: Hero | undefined): 'drop' | 'take' | null => {
  const p = s.plunder;
  if (!p || !hero || hero.downTicks > 0 || s.result !== 'running') return null;
  if (hero.carry !== undefined) return 'drop';
  return p.caches.some((c) => c.carriers.length < (c.kind === 'coffer' ? 2 : 1) && near(hero, c, PLUNDER.reach)) ? 'take' : null;
};

/** Whether the leave button works now: everyone still standing by the cart. */
export const plunderLeaveReady = (s: BattleState): boolean => !!s.plunder && !s.plunder.end && s.result === 'running' && plunderCanLeave(s);

type Way = { x: number; y: number; label: string };

/**
 * A plunder's arrows (docs/MISIONES.md, «Interfaz»): to the cart with a load in hand (or to go home), to a cache a
 * dragger is hauling off, and to the nearest cache lying free. Over each place in view, or held at the edge.
 */
export class PlunderWays {
  readonly el = h('div.dg-hud.pl-ways');
  private els: HTMLElement[] = [];

  constructor(
    private s: () => BattleState,
    private me: string,
  ) {}

  private targets(s: BattleState): Way[] {
    const p = s.plunder;
    const hero = s.heroes.find((x) => x.pid === this.me);
    if (!p || !hero || hero.downTicks > 0 || s.result !== 'running') return [];
    const out: Way[] = [];
    if (hero.carry !== undefined) return [{ x: 0, y: 0, label: t('plunder.way.cart') }];
    const stolen = p.caches.find((c) => c.dragger);
    if (stolen) out.push({ x: stolen.x, y: stolen.y, label: t('plunder.way.stolen') });
    const half = p.caches.find((c) => c.kind === 'coffer' && c.carriers.length === 1);
    if (half) out.push({ x: half.x, y: half.y, label: t('plunder.way.coffer') });
    let best: PlunderCache | undefined;
    let bd = Infinity;
    for (const c of p.caches) {
      if (c.carriers.length || c.dragger) continue;
      const d = (c.x - hero.x) ** 2 + (c.y - hero.y) ** 2;
      if (d < bd) {
        bd = d;
        best = c;
      }
    }
    if (best && best !== half) out.push({ x: best.x, y: best.y, label: t(best.kind === 'coffer' ? 'plunder.way.coffer' : 'plunder.way.chest') });
    if (p.loaded >= PLUNDER.fullLoad || !best) out.push({ x: 0, y: 0, label: t('plunder.way.home') });
    return out;
  }

  place(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    const s = this.s();
    const mine = s.heroes.find((x) => x.pid === this.me);
    const mp = mine ? positions.get(mine.id) : undefined;
    const ways = this.targets(s);
    const W = viewW();
    const H = viewH();
    const v = new THREE.Vector3();
    ways.forEach((w, k) => {
      let el = this.els[k];
      if (!el) {
        el = h('div.dg-way', {}, h('span.dg-way-arrow', { html: CHEVRON }), h('span.dg-way-txt'));
        this.el.append(el);
        this.els[k] = el;
      }
      const p = simToWorld(w.x, w.y);
      // Already there: no need to point.
      const close = !!mp && Math.hypot(mp.x - p.x, mp.z - p.z) < WORLD_PER_HEX * 1.2;
      el.hidden = close;
      if (close) return;
      (el.querySelector('.dg-way-txt') as HTMLElement).textContent = w.label;
      v.copy(p).setY(1.2).project(camera);
      const behind = v.z > 1;
      let px = (v.x * 0.5 + 0.5) * W;
      let py = (-v.y * 0.5 + 0.5) * H;
      if (behind) {
        px = W - px;
        py = H - py;
      }
      const m = 46;
      const top = W <= 640 ? 200 : 120;
      const inView = !behind && px > m && px < W - m && py > top && py < H - m;
      el.classList.toggle('edge', !inView);
      let turn = 0;
      if (!inView) {
        const cx = W / 2;
        const cy = (top + H - m) / 2;
        const dx = px - cx;
        const dy = py - cy;
        const k2 = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), (H - m - top) / 2 / Math.max(1, Math.abs(dy)));
        px = cx + dx * k2;
        py = cy + dy * k2;
        turn = Math.atan2(dy, dx) - Math.PI / 2;
      }
      el.style.transform = `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) translate(-50%, -50%)`;
      (el.querySelector('.dg-way-arrow') as HTMLElement).style.transform = `rotate(${turn.toFixed(3)}rad)`;
    });
    for (let k = ways.length; k < this.els.length; k++) (this.els[k] as HTMLElement).hidden = true;
  }
}
